import { useEffect, useState } from "react";
import "./AdminGalleryManager.css";
import { ref, listAll, getDownloadURL, uploadBytes, deleteObject } from "firebase/storage";
import { storage } from "../../firebase";
import Toast from "../Toast/Toast";

export default function AdminGalleryManager() {
  const [newImages, setNewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "info") => setToast({ message, type });

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const folderRef = ref(storage, "adminGallery");
      const result = await listAll(folderRef);
      const urls = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return { name: itemRef.name, url, ref: itemRef };
        })
      );
      setExistingImages(urls);
    } catch (error) {
      console.error("Failed to load gallery images:", error);
    }
  };

  const generateUniqueName = (filename) => {
    const timestamp = Date.now();
    const extension = filename.substring(filename.lastIndexOf("."));
    const name = filename.substring(0, filename.lastIndexOf("."));
    return `${name}_${timestamp}${extension}`;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setNewImages((prev) => [...prev, ...previews]);
  };

  const uploadImages = async () => {
    try {
      const uploads = newImages.map(async (img) => {
        const uniqueName = generateUniqueName(img.file.name);
        const imageRef = ref(storage, `adminGallery/${uniqueName}`);
        await uploadBytes(imageRef, img.file);
        return getDownloadURL(imageRef);
      });

      await Promise.all(uploads);
      showToast("Gallery images uploaded successfully!", "success");
      setNewImages([]);
      fetchGalleryImages();
    } catch (error) {
      console.error("Upload failed:", error);
      showToast("An error occurred during upload.", "error");
    }
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const deleteExistingImage = async (imageRef, name) => {
    try {
      await deleteObject(imageRef);
      showToast(`Deleted ${name}`, "success");
      fetchGalleryImages();
    } catch (error) {
      console.error("Delete failed:", error);
      showToast("An error occurred during deletion.", "error");
    }
  };

  return (
    <div className="admin-content">
      <div className="admin-gallery-header">
        <div>
          <h2 className="admin-section-title">Homepage Gallery</h2>
          <p className="admin-section-subtitle">
            Upload images to showcase on your homepage gallery. Drag & drop or click to select multiple.
          </p>
        </div>
        <div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="file-input"
          />
        </div>
      </div>

      {newImages.length > 0 && (
        <div className="admin-gallery-toolbar">
          <span>{newImages.length} new image{newImages.length > 1 ? "s" : ""} selected</span>
          <button className="upload-button" onClick={uploadImages}>
            Upload All
          </button>
        </div>
      )}

      {newImages.length > 0 && (
        <div className="preview-grid">
          {newImages.map((img, index) => (
            <div className="preview-card" key={index}>
              <img src={img.url} alt={`Preview ${index}`} />
              <button onClick={() => removeNewImage(index)}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {existingImages.length > 0 && (
        <div className="gallery-section">
          <h3 className="admin-section-title">Current Gallery</h3>
          <div className="preview-grid">
            {existingImages.map((img, index) => (
              <div className="preview-card" key={index}>
                <img src={img.url} alt={`Existing ${index}`} />
                <button onClick={() => deleteExistingImage(img.ref, img.name)}>Delete</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
